import { useState } from 'react';
import { commentThreads } from '@/mocks/home';
import Reveal from '@/components/feature/Reveal';

type TabKey = 'all' | 'pro' | 'con';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: '全部观点' },
  { key: 'pro', label: '正方 · 有戏' },
  { key: 'con', label: '反方 · 冷静' },
];

export default function CommentArena() {
  const [tab, setTab] = useState<TabKey>('all');
  const [liked, setLiked] = useState<Record<string, boolean>>({});

  const list = tab === 'all' ? commentThreads : commentThreads.filter((item) => item.side === tab);

  return (
    <section id="comments" className="w-full bg-background-100 py-12 md:py-16">
      <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
        <Reveal>
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="text-xs font-medium tracking-widest text-accent-700">COMMENTS / 评论现场</span>
              <h3 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                同一件事，评论区给出两种答案
              </h3>
              <p className="mt-2 max-w-2xl text-sm text-foreground-600">
                观点在这里是并列的。切换标签，看看支持者和冷静派各自抓到了什么细节。
              </p>
            </div>
            <div className="flex w-fit items-center gap-1 rounded-full border border-background-300 bg-background-50 px-1 py-1">
              {TABS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                    tab === item.key
                      ? 'bg-foreground-900 text-background-50'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-7 grid grid-cols-1 gap-4 md:grid-cols-2 lg:gap-5">
            {list.map((item) => {
              const isPro = item.side === 'pro';
              return (
                <article
                  key={item.id}
                  className={`flex flex-col rounded-lg border p-4 transition-colors md:p-5 ${
                    isPro ? 'border-primary-200 bg-primary-50' : 'border-secondary-200 bg-secondary-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-background-200">
                      <img src={item.avatar} alt={item.user} className="h-full w-full object-cover object-top" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground-900">{item.user}</p>
                      <span
                        className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[11px] ${
                          isPro ? 'bg-primary-200 text-primary-900' : 'bg-secondary-200 text-secondary-900'
                        }`}
                      >
                        {isPro ? '正方 · 有戏派' : '反方 · 冷静派'}
                      </span>
                    </div>
                  </div>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-800">{item.text}</p>
                  <div className="mt-4 flex items-center gap-3 border-t border-background-200/70 pt-3">
                    <button
                      type="button"
                      onClick={() => setLiked((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                      className={`flex cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-1 text-xs transition-colors ${
                        liked[item.id]
                          ? 'bg-accent-500 text-background-50'
                          : 'bg-background-50/80 text-foreground-700 hover:text-accent-700'
                      }`}
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className={`${liked[item.id] ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-xs`} />
                      </span>
                      {liked[item.id] ? Number(item.likes) + 1 : item.likes}
                    </button>
                    <button
                      type="button"
                      className="flex cursor-pointer items-center gap-1.5 rounded-md bg-background-50/80 px-2.5 py-1 text-xs text-foreground-700 transition-colors hover:text-foreground-950"
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-reply-line text-xs" />
                      </span>
                      回复
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
}